import { Check } from "lucide-react";
import type { FC, ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";

type Props = {
  isDirty: boolean;
  isSubmitting: boolean;
  /** Extra gate on Save (e.g. data still loading). */
  canSubmit?: boolean;
  /** Reverts the draft; omitted hides the button. */
  onCancel?: () => void;
  /**
   * Explicit submit handler. When omitted the button is `type="submit"` and
   * relies on being rendered inside a `<Form>`.
   */
  onSubmit?: () => void;
  submitLabel?: string;
  cancelLabel?: string;
  /** Left-hand slot (secondary actions, hints). */
  leading?: ReactNode;
  className?: string;
};

/** Cancel / Save bar pinned under a page-style form; lifts when there's something to save. */
export const StickyFormFooter: FC<Props> = ({
  isDirty,
  isSubmitting,
  canSubmit = true,
  onCancel,
  onSubmit,
  submitLabel = "Save changes",
  cancelLabel = "Cancel",
  leading,
  className
}) => (
  <div
    data-slot="sticky-form-footer"
    className={cn(
      "flex shrink-0 items-center justify-between gap-3 border-t border-border bg-background px-4 py-2",
      isDirty && "shadow-[0_-4px_12px_-6px_rgba(0,0,0,0.15)]",
      className
    )}
  >
    <div className="flex min-w-0 items-center gap-2">{leading}</div>
    <div className="flex shrink-0 items-center gap-2">
      {isDirty ? (
        <span className="hidden text-xs text-muted-foreground sm:inline">
          Unsaved changes
        </span>
      ) : null}
      {onCancel ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onCancel}
          disabled={!isDirty || isSubmitting}
        >
          {cancelLabel}
        </Button>
      ) : null}
      <Button
        variant="primary"
        type={onSubmit ? "button" : "submit"}
        size="sm"
        className="gap-1.5"
        onClick={onSubmit}
        disabled={!canSubmit || !isDirty || isSubmitting}
      >
        {isSubmitting ? (
          <Spinner className="size-3.5" />
        ) : (
          <Check className="size-3.5" />
        )}
        {isSubmitting ? "Saving…" : submitLabel}
      </Button>
    </div>
  </div>
);
